import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  faBorderBottom,
  faBorderLeft,
  faBorderRight,
  faBorderTop,
} from "@fortawesome/pro-regular-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import type { Padding } from "../../domain/types";
import { EMAIL_DOCUMENT_MAX_PADDING } from "../../schema/emailDocumentContract";
import { emailBuilderSpacingIsUniform } from "../../shell/panel/blockSettings/emailBuilderBlockSettingsSpacing";
import { isPaddingInactive } from "../settingsSectionActivation";
import { EmailBuilderSettingsFieldGrid, EmailBuilderSettingsFieldGridRow } from "./EmailBuilderSettingsFieldGrid";
import { EmailBuilderSettingsIconNumberField } from "./EmailBuilderSettingsIconNumberField";
import { EmailBuilderSettingsRadioGroup } from "./EmailBuilderSettingsRadioGroup";
import { EmailBuilderSettingsSliderField } from "./EmailBuilderSettingsSliderField";
import { useEmailBuilderSettingsPanelScrollOnChange } from "../useEmailBuilderSettingsPanelScrollOnChange";

const EMAIL_BUILDER_PADDING_SIDE_ICONS: Record<"top" | "right" | "bottom" | "left", IconDefinition> = {
  top: faBorderTop,
  right: faBorderRight,
  bottom: faBorderBottom,
  left: faBorderLeft,
};

type EmailBuilderSettingsPaddingSectionProps = {
  fieldLabel: string;
  value: Padding;
  defaultValue: Padding;
  onChange: (padding: Padding, historyGroupId?: string | null) => void;
};

type TPaddingMode = "uniform" | "custom";

function resolvePaddingMode(value: Padding): TPaddingMode {
  return emailBuilderSpacingIsUniform(value) ? "uniform" : "custom";
}

export function EmailBuilderSettingsPaddingSection(props: EmailBuilderSettingsPaddingSectionProps) {
  const { t } = useTranslation();
  const { fieldLabel, ...modelProps } = props;
  const { value } = modelProps;
  const allSidesUniform = emailBuilderSpacingIsUniform(value);
  const [mode, setMode] = useState<TPaddingMode>(() => resolvePaddingMode(value));
  const wasInactiveRef = useRef(isPaddingInactive(value));
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEmailBuilderSettingsPanelScrollOnChange(sectionRef, mode);

  useEffect(() => {
    const isInactive = isPaddingInactive(value);
    if (wasInactiveRef.current && !isInactive) {
      setMode(resolvePaddingMode(value));
    }
    wasInactiveRef.current = isInactive;
  }, [value]);

  const sideLabels = {
    top: t("Top"),
    bottom: t("Bottom"),
    left: t("Left"),
    right: t("Right"),
  } as const;

  return (
    <div ref={sectionRef} className="email-builder-settings-padding-section">
      <EmailBuilderSettingsRadioGroup
        name="email-builder-padding-mode"
        value={mode}
        options={[
          { value: "uniform", label: t("All sides") },
          { value: "custom", label: t("Customize each side") },
        ]}
        ariaLabel={t("Padding mode")}
        onChange={nextMode => {
          setMode(nextMode);
        }}
      />
      {mode === "uniform" ? (
        <EmailBuilderSettingsSliderField
          label={fieldLabel}
          min={0}
          max={EMAIL_DOCUMENT_MAX_PADDING}
          value={value.top}
          mixed={!allSidesUniform}
          onChange={(next, historyGroupId) => {
            modelProps.onChange(
              { top: next, right: next, bottom: next, left: next },
              historyGroupId,
            );
          }}
        />
      ) : (
        <EmailBuilderSettingsFieldGrid>
          <EmailBuilderSettingsFieldGridRow>
            {(["top", "bottom"] as const).map(side => (
              <EmailBuilderSettingsIconNumberField
                key={side}
                label={t("{{side}} (px)", { side: sideLabels[side] })}
                icon={EMAIL_BUILDER_PADDING_SIDE_ICONS[side]}
                value={value[side]}
                min={0}
                max={EMAIL_DOCUMENT_MAX_PADDING}
                onChange={(next, historyGroupId) => {
                  modelProps.onChange({ ...value, [side]: next }, historyGroupId);
                }}
              />
            ))}
          </EmailBuilderSettingsFieldGridRow>
          <EmailBuilderSettingsFieldGridRow>
            {(["left", "right"] as const).map(side => (
              <EmailBuilderSettingsIconNumberField
                key={side}
                label={t("{{side}} (px)", { side: sideLabels[side] })}
                icon={EMAIL_BUILDER_PADDING_SIDE_ICONS[side]}
                value={value[side]}
                min={0}
                max={EMAIL_DOCUMENT_MAX_PADDING}
                onChange={(next, historyGroupId) => {
                  modelProps.onChange({ ...value, [side]: next }, historyGroupId);
                }}
              />
            ))}
          </EmailBuilderSettingsFieldGridRow>
        </EmailBuilderSettingsFieldGrid>
      )}
    </div>
  );
}
